/**
 *  Header
 *    Scripts used for the header section.
 *
 *  Version
 *    1.0.0 - 2022/07/22
 */

import {
  CLASS_HAS_FIXED_HEADER,
  CLASS_STICKY,
  CLASS_VISIBLE,
  CLASS_SCROLLING,
  CLASS_MENU_OPEN,
  CLASS_ACTIVE, 
  CLASS_OPEN
} from "../../tools/constants/classes";
import {
  cartGetCurrent,
  ON_CART_FINISHED,
} from "@process-creative/slate-theme-tools";
import { Section } from "../../tools/section";
import { Menu, SELECTOR_MENU } from "./menu";
import { documentDisableScroll, documentEnableScroll } from "../../tools/document";
import { responsiveMediaAbove } from "../../settings/responsive";


const SELECTOR_CONTAINER = "[data-header]";
const SELECTOR_CART_COUNT = "[data-cart-counter]";
const SELECTOR_TOPBAR = '[data-topbar]';
const SELECTOR_SEARCH_TRIGGER = '[data-search-trigger]';
const SELECTOR_SEARCH_DRAWER = '[data-search-drawer]';
const SELECTOR_SEARCH_CLOSE = '[data-search-close]';
const SELECTOR_SEARCH_INPUT = '[data-search-input]';

class Header extends Section {
  private topBar: HTMLElement | null = null;
  private topBarHeight: number = 0;
  private cartCountElement: HTMLElement | null = null;
  private lastPosition: number = 0;
  private menuElement: HTMLElement | null = null;
  private searchTrigger: HTMLElement | null = null;
  private searchDrawer: HTMLElement | null = null;
  private lastKnownScrollPosition = window.scrollY;
  private searchCloses:NodeListOf<HTMLElement> | null = null;
  private searchInput:HTMLInputElement | null = null;

  

  queryChildren(container: HTMLElement) {
    this.cartCountElement = container.querySelector(SELECTOR_CART_COUNT);
    this.topBar = document.querySelector(SELECTOR_TOPBAR);
    this.menuElement = container.querySelector(SELECTOR_MENU);
    this.searchTrigger = container.querySelector(SELECTOR_SEARCH_TRIGGER);
    this.searchDrawer = container.querySelector(SELECTOR_SEARCH_DRAWER) || document.querySelector(SELECTOR_SEARCH_DRAWER);
     if(this.searchDrawer) {
      this.searchCloses = this.searchDrawer.querySelectorAll(SELECTOR_SEARCH_CLOSE);
      this.searchInput = this.searchDrawer.querySelector(SELECTOR_SEARCH_INPUT);
    }
  }

  attachEvents() {
    if (this.topBar) {
      window.addEventListener("resize", this.onScreenSizeChange);
      this.topBarHeight = this.topBar.offsetHeight;
    }

    // Check header position on load to handle existing scroll position
    this.initialHeaderCheck();

    this.searchTrigger?.addEventListener('click', (e) => this.onSearchClick(e));
    this.searchCloses?.forEach(close => {
      close.addEventListener('click', (e) => this.onSearchCloseClick(e));
    })

    // Setup menu
    if (this.menuElement) new Menu(this.menuElement, this.container);

    // Initialize cart counter state
    this.onCartUpdate();

    window.addEventListener(ON_CART_FINISHED, this.onCartUpdate);
    document.addEventListener("scroll", (e) => this.onScroll(e));
  }

  detatchEvents() {
    if (this.topBar) {
      window.removeEventListener("resize", this.onScreenSizeChange);
    }
    window.removeEventListener(ON_CART_FINISHED, this.onCartUpdate);
    document.removeEventListener("scroll", this.onScroll);
  }

  initialHeaderCheck() {
    const topbarHeight = this.topBar?.clientHeight;
    const headerHeight = this.container.clientHeight;
    const stickyDistance = headerHeight + (topbarHeight ? topbarHeight : 0);
    const body = document.body!;

    // Works on both mobile and desktop
    if (document.documentElement.scrollTop > stickyDistance) {
        this.container.classList.add(CLASS_VISIBLE);
        this.container.classList.add(CLASS_STICKY);
    }
  }

  onSearchClick = (event:MouseEvent) => {
    event.preventDefault();
    this.searchDrawer?.classList.add(CLASS_ACTIVE);
    this.searchDrawer?.setAttribute('data-lenis-prevent', '');
    documentDisableScroll();
    this.searchInput?.focus();
  }

  onSearchCloseClick = (event:MouseEvent) => {
    event.preventDefault();
    this.searchDrawer?.classList.remove(CLASS_ACTIVE);
    this.searchDrawer?.removeAttribute('data-lenis-prevent');
    documentEnableScroll();
  }

  onScroll = (e?: Event) => {
    this.checkHeaderPosition(); // Call it directly when scrolling
    
    // Works on both mobile and desktop
    if (window.scrollY < this.lastKnownScrollPosition && window.scrollY > 0) {
      document.body.classList.add(CLASS_HAS_FIXED_HEADER);
    } else {
      document.body.classList.remove(CLASS_HAS_FIXED_HEADER);
    }

    this.lastKnownScrollPosition = window.scrollY;
  }

  checkHeaderPosition() {
    const top = document.documentElement.scrollTop;
    const topbarHeight = this.topBar?.clientHeight;
    const headerHeight = this.container.clientHeight;
    const stickyDistance = headerHeight + (topbarHeight ? topbarHeight : 0);
    const body = document.body!;

    // Sticky header works on both mobile and desktop
    if(top != this.lastPosition) {
      // Are we scrolling up or down?
      if (top < this.lastPosition) { // Scrolling up
        this.container.classList.add(CLASS_VISIBLE);
      } else if (top > this.lastPosition) { // Scrolling down
        this.container.classList.remove(CLASS_VISIBLE);
      }

      if(top > this.topBarHeight) {
        this.topBar?.classList.add(CLASS_SCROLLING);
        this.container.classList.remove(CLASS_MENU_OPEN);
      } else {
        this.topBar?.classList.remove(CLASS_SCROLLING);
        this.container.classList.remove(CLASS_VISIBLE);
        this.container.classList.remove(CLASS_STICKY);
      }
      
      if (top > stickyDistance) {
        this.container.classList.add(CLASS_STICKY);
      }
      
      this.lastPosition = top;
    }

    // Remove the continuous loop - no more requestAnimationFrame here
  }

  onScreenSizeChange = () => {
    // Only needed if your topBar changes height at different screens
    this.topBarHeight = this.topBar?.offsetHeight || 0;
  };

  onCartUpdate = () => {
    if (!this.cartCountElement) return;
    const count = cartGetCurrent()["item_count"] || 0;
    if (count > 0) {
      this.cartCountElement.innerText = count.toString();
      this.cartCountElement.removeAttribute("data-count");
    } else {
      this.cartCountElement.innerText = "";
      this.cartCountElement.setAttribute("data-count", "0");
    }
  };
}

Section.register(SELECTOR_CONTAINER, Header);
